import React, { Component } from 'react';
import _ from 'lodash';
import DataList from 'components/ui/DataList';
import DataPoller from 'components/ui/DataPoller';
import CargomaticInput from 'components/v2/input';
import AccessorialRequestActionCreators from 'actions/AccessorialRequestActionCreators';
import ExceptionStore from 'stores/ExceptionStore';
import SessionStore from 'stores/SessionStore';
import { withTranslation } from 'react-i18next';
import AccessorialsMapping from './AccessorialMapping';
import Alert from '../../ui/Alert';
import Container from '../../v2/container';

class AccessorialModule extends Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: {
        per_page: 25,
        paginate: true,
        page: 1,
      },
      totalCount: 0,
      totalPages: 0,
      updates: [],
      exceptions: [],
      polling_frequency: 5000,
    };
  }

  componentDidMount() {
    ExceptionStore.addChangeListener(this.onChange);
    AccessorialRequestActionCreators.getAccessorialRequests(this.getRequest());
  }

  componentWillUnmount() {
    ExceptionStore.removeChangeListener(this.onChange);
  }

  getRequest() {
    if (SessionStore.isAdmin() || SessionStore.isCarrier()) {
      return {
        shipper_status: 'pending-shipper',
        payee_type: 'carrier',
      };
    }
    return {
      shipper_status: 'pending-shipper',
      shipper: SessionStore.getUser()._id,
      ...this.state.filters,
    };
  }
  
  handlePaginationSelect(page) {
    this.setState(
      (prevState) => {
        const filters = prevState.filters;
        filters.page = page;
        return {
          selected: [],
          filters,
          page,
        };
      },
      () => {
        AccessorialRequestActionCreators.getAccessorialRequests(this.getRequest());
      },
    );
  }

  onChange = () => {
    const result = ExceptionStore.getAllAccessorialPaginatedRequests();
    let { items: exceptions, totalCount, totalPages,  } = result;

    exceptions = exceptions.map((exception) => {
      const currentException = this.state.exceptions.find(
        (exc) => exc._id === exception._id,
      );
      return {
        ...exception,
        toggleDeclineAccept: this.toggleDeclineAccept.bind(this),
        handleComment: this.handleComment.bind(this),
        acceptChecked: currentException
          ? currentException.acceptChecked
          : false,
        declineChecked: currentException
          ? currentException.declineChecked
          : false,
      };
    });
    this.setState({ exceptions, totalCount, totalPages });
  };

  submitAll() {
    const { exceptions, updates } = this.state;
    const accepts = _.filter(exceptions, (ex) => ex.acceptChecked);
    const declines = _.filter(exceptions, (ex) => ex.declineChecked);

    if (accepts.length) {
      const payload = {
        accessorials: accepts.map((acc) => {
          return { _id: acc._id, shipment: acc.shipment };
        }),
        resolution_type: 'accept',
        sendEmail: true,
      };
      AccessorialRequestActionCreators.acceptAccessorials(payload);
    }
    if (declines.length) {
      const payload = declines.map((dec) => {
        const update = _.find(updates, (up) => up._id === dec._id) || {};
        return {
          _id: dec._id,
          shipper_status: 'declined',
          resolution_type: 'decline',
          admin_note: update.admin_note || '',
        };
      });
      AccessorialRequestActionCreators.updateAccessorialRequest(payload);
    }
  }

  handleComment = (value, accessorialId) => {
    const { updates } = this.state;
    let update = _.find(updates, { _id: accessorialId });
    if (!update) {
      update = { _id: accessorialId, admin_note: value };
      updates.push(update);
    } else {
      update.admin_note = value;
    }
    this.setState({ updates });
  };

  toggleDeclineAccept = (event, toAccept, accessorialId) => {
    const { exceptions } = this.state;
    const exception = _.find(exceptions, { _id: accessorialId });
    if (exception) {
      this.setCheckedStatus(exception, toAccept, event);
      this.setState({ exceptions });
    }
  };

  setCheckedStatus = (update, toAccept, event) => {
    if (toAccept) {
      update.acceptChecked = event.target.checked;
      if (update.acceptChecked) {
        update.declineChecked = false;
      }
    } else {
      update.declineChecked = event.target.checked;
      if (update.declineChecked) {
        update.acceptChecked = false;
      }
    }
  };

  renderSubmitButton() {
    const { t } = this.props;
    const { exceptions } = this.state;
    const selectedItems = _.filter(
      exceptions,
      (update) => update.acceptChecked || update.declineChecked,
    );
    return (
      <div className="flex items-baseline justify-end p-4">
        <button
          type="button"
          className="button button-green"
          disabled={!selectedItems.length}
          onClick={() => this.submitAll()}
        >
          <span>{t('SUBMIT')}</span>
        </button>
      </div>
    );
  }

  toAccept = (cell, row) => {
    return this.checkBoxFormatter(cell, row, true);
  };

  toDecline = (cell, row) => {
    return this.checkBoxFormatter(cell, row, false);
  };

  checkBoxFormatter = (_cell, row, acceptChecked) => {
    return (
      <input
        type="checkbox"
        label={null}
        checked={acceptChecked ? row.acceptChecked : row.declineChecked}
        onChange={(event) => {
          this.toggleDeclineAccept(event, acceptChecked, row?._id);
        }}
      />
    );
  };

  commentFormatter = (_cell, row) => {
    const { t } = this.props;
    return (
      <CargomaticInput
        type="text"
        hideLabel
        placeholder={t('COMMENT')}
        onChange={(value) => {
          this.handleComment(value, row._id);
        }}
      />
    );
  };

  getAccessorialsMappingColumns = () => {
    return AccessorialsMapping?.columns?.map((col) => {
      if (col.dataField === 'acceptChecked') {
        return { ...col, formatter: this.toAccept };
      }

      if (col.dataField === 'declineChecked') {
        return { ...col, formatter: this.toDecline };
      }

      if (col.dataField === 'comment') {
        return { ...col, formatter: this.commentFormatter };
      }
      return col;
    });
  };

  render() {
    const { t } = this.props;
    const { exceptions, polling_frequency } = this.state;

    if (!SessionStore.isAdmin() && !SessionStore.isShipper()) {
      return (
        <div className="p-4">
          <Alert title={t('REQUIRE_PERMISSION_TO_VIEW_PAGE')} type="error" />
        </div>
      );
    }

    return (
      <Container className="py-8 overflow-y-auto">
        <h3 className="my-0 mb-4">{t('ACCESSORIAL_REQUESTS')}</h3>
        <DataPoller
          action={() => {
            AccessorialRequestActionCreators.getAccessorialRequests(
              this.getRequest(),
            );
          }}
          frequency={polling_frequency}
        />
        <DataList
          striped
          showHeaders
          paginate
          total={this.state.totalCount}
          pages={this.state.totalPages}
          page={this.state.filters.page}
          perPage={this.state.filters.per_page}
          onPaginationSelect={(page) => this.handlePaginationSelect(page)}
          data={exceptions}
          columns={this.getAccessorialsMappingColumns()}
          noDataIndication={t('ACCESSORIAL_REQUESTS_NOT_FOUND')}
        />
        {this.renderSubmitButton()}
      </Container>
    );
  }
}

export default withTranslation()(AccessorialModule);
